"use client";
import { motion } from "framer-motion";
import { ShieldCheck, Receipt, Users, FileSpreadsheet, BookOpen, ClipboardList } from "lucide-react";
import { TOTAL_FEATURES, TOTAL_CATEGORIES } from "./featureData";
import { useLanguage } from "@/contexts/LanguageContext";
import RichText from "./RichText";

/* Icons stay here, copy lives in the dictionary — the order of the two lists
   is the contract between them. */
const PROBLEM_ICONS = [Receipt, Users, FileSpreadsheet, BookOpen, ClipboardList];

export default function TheProblem() {
  const { t, tv } = useLanguage();

  const problems = tv<string[]>("problem.items");

  // Every figure here is read from the feature catalogue's single source of
  // truth or from the five role dashboards we actually ship, so none of them
  // can drift out of sync with the product or overstate our customer count.
  const proofPoints = [
    { value: String(TOTAL_FEATURES), label: t("problem.proofFeatures") },
    { value: String(TOTAL_CATEGORIES), label: t("problem.proofModules") },
    { value: "5", label: t("problem.proofDashboards") },
  ];

  return (
    <section id="the-problem" className="w-full scroll-mt-24 py-12 sm:py-16 sm:py-20 lg:py-28 bg-gradient-to-b from-[#EFF3FE] via-[#F8FAFC] to-white overflow-hidden relative flex items-center">
      
      {/* Background Lighting Accents */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#2A48C9]/[0.05] rounded-full blur-[120px] pointer-events-none -translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-[#2A48C9]/[0.06] rounded-full blur-[140px] pointer-events-none translate-x-1/3 translate-y-1/3" />

      {/* Soft Wavy Background Shapes */}
      <div className="absolute bottom-0 left-0 w-full pointer-events-none z-0 opacity-40">
        <svg viewBox="0 0 1440 200" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <path d="M0,128L48,138.7C96,149,192,171,288,165.3C384,160,480,128,576,117.3C672,107,768,117,864,133.3C960,149,1056,171,1152,170.7C1248,171,1344,149,1392,138.7L1440,128L1440,200L1392,200C1344,200,1248,200,1152,200C1056,200,960,200,864,200C768,200,672,200,576,200C480,200,384,200,288,200C192,200,96,200,48,200L0,200Z" fill="#D6E0FC" />
        </svg>
      </div>

      <div className="site-container relative z-10">
        <div className="flex flex-col lg:flex-row items-center lg:items-stretch gap-10 lg:gap-16">
          
          {/* Left Column: Heading & 5 Problem Cards */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.6 }}
            className="w-full lg:w-[50%] flex flex-col justify-center"
          >
            <h2 className="font-heading section-heading text-[#0d1117] mb-6 sm:mb-8">
              <RichText text={t("problem.heading")} breakClassName="hidden sm:block" />
            </h2>

            <div className="flex flex-col gap-3 sm:gap-3.5">
              {problems.map((text, i) => {
                const Icon = PROBLEM_ICONS[i];
                return (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ delay: 0.08 * i, duration: 0.5 }}
                    whileHover={{ scale: 1.015, x: 6 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-3.5 sm:gap-4 bg-white p-3.5 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-300 cursor-pointer border border-slate-100/80 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-lg hover:border-[#3D5EE1]/30 group"
                  >
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#2A48C9]/10 flex items-center justify-center shrink-0 group-hover:bg-[#3D5EE1] transition-colors duration-300">
                      <Icon size={18} className="text-[#3D5EE1] group-hover:text-white transition-colors duration-300" strokeWidth={2} />
                    </div>
                    <span className="text-[13.5px] sm:text-[14.5px] font-medium text-slate-800 tracking-tight leading-snug">
                      {text}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right Column: Premium Success Standard Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            whileHover={{ y: -6, scale: 1.01 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="w-full lg:w-[50%] bg-white p-6 sm:p-8 lg:p-10 rounded-[28px] sm:rounded-[36px] shadow-[0_20px_50px_rgba(61,94,225,0.08)] relative mt-4 lg:mt-0 border border-slate-100 flex flex-col justify-center"
          >
            {/* Top Ribbon Shield Badge */}
            <div 
               className="absolute -top-5 right-8 sm:right-10 w-16 sm:w-18 h-24 sm:h-26 bg-[#3D5EE1] flex flex-col items-center justify-center pb-3 z-20 shadow-md"
               style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 85%, 0 100%)" }}
            >
               <div className="w-full h-3 bg-[#172B88] absolute top-0 left-0" />
               <div className="relative z-10 w-10 h-10 rounded-full border-2 border-white flex items-center justify-center mt-2 bg-[#3D5EE1]">
                 <ShieldCheck size={20} className="text-white" strokeWidth={2} />
               </div>
            </div>

            <div className="relative z-10 pt-2">
               <h3 className="text-2xl sm:text-3xl lg:text-[32px] font-light text-[#0d1117] leading-[1.35]">
                 {t("problem.cardTitleLine1")}
               </h3>
               <h3 className="text-2xl sm:text-3xl lg:text-[32px] font-semibold text-[#3D5EE1] mb-4 leading-[1.35]">
                 {t("problem.cardTitleLine2")}
               </h3>

               <p className="text-[13.5px] sm:text-[14.5px] text-slate-500 font-medium leading-relaxed mb-6">
                 {t("problem.cardBody")}
               </p>

               {/* This block used to be a five-star customer quote, attributed
                   to a director and carrying a "Verified" badge. The words were
                   written in-house, not said by a school, so the review framing
                   has been dropped and the copy now speaks in our own voice.
                   Restore the stars, attribution and badge only with a real
                   customer's words and their written permission. */}
               <div>
                  <div className="flex items-center gap-3 mb-4">
                     <span className="text-[10.5px] font-bold text-slate-400 uppercase tracking-wider shrink-0">{t("problem.shiftLabel")}</span>
                     <div className="h-px flex-1 bg-slate-100" />
                  </div>

                  <motion.div
                     initial={{ opacity: 0, x: -12 }}
                     whileInView={{ opacity: 1, x: 0 }}
                     viewport={{ once: true, amount: 0.1 }}
                     transition={{ delay: 0.1, duration: 0.45 }}
                     className="relative rounded-2xl sm:rounded-3xl border border-[#D6E0FC] bg-[#EFF3FE] p-5 sm:p-6 hover:border-[#3D5EE1]/40 transition-colors duration-300"
                  >
                     <p className="text-[12.5px] sm:text-[13.5px] text-slate-700 leading-relaxed">
                        {t("problem.shiftBody")}
                     </p>
                  </motion.div>
               </div>

               {/* Product proof — figures the reader can go and check for
                   themselves, rather than a customer count we can't stand behind. */}
               <dl className="mt-5 sm:mt-6 grid grid-cols-3 divide-x divide-slate-100 border-t border-slate-100 pt-4 sm:pt-5">
                  {proofPoints.map((point) => (
                     <div key={point.label} className="px-2 first:pl-0 last:pr-0 text-center first:text-left last:text-right">
                        <dt className="sr-only">{point.label}</dt>
                        <dd>
                           <span className="block text-xl sm:text-2xl font-semibold text-[#0d1117] leading-none tracking-tight">
                              {point.value}
                           </span>
                           <span className="mt-1.5 block text-[10px] sm:text-[10.5px] font-medium text-slate-500 uppercase tracking-wider leading-tight">
                              {point.label}
                           </span>
                        </dd>
                     </div>
                  ))}
               </dl>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

